import { ATGSFLResponse } from 'types/ATGSFLResponse';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import { atgOrigin } from '../../utilities/util';
import { startSpan, endSpan, failSpan } from '../../utilities/loggerv2/performanceSpans';

const baseURL = (window: Window, action: string, sessionConfirmationNumber: string, productId = '') =>
  `${atgOrigin(window)}/rest/model/sdg/savedforlater/SavedForLaterActor/${action}?_dynSessConf=${sessionConfirmationNumber}`.concat(
    productId !== '' ? `&productId=${productId}` : '',
  );

const loggerSFL = LoggerFactory.getLogger('Save for later service');

const makeRequestToSaveForLaterATG = async (
  window: Window,
  csrfToken: string,
  sessionConfirmationNumber: string,
  action = '',
  productId = '',
  methodType = 'GET',
) => {
  const spanId = startSpan('saveForLater', { productSku: productId });
  const response = await fetch(baseURL(window, action, sessionConfirmationNumber, productId), {
    headers: {
      'Content-Type': 'application/json',
      'x-csrf-token': csrfToken,
    },
    method: methodType,
    body: null,
  });
  const responseOk = response.ok !== undefined ? response.ok : true;

  if (!responseOk) {
    const errorMessage = `HTTP error ${response.status}: ${response.statusText}`;
    (await loggerSFL).logWithCode(ErrorCodes.SAVE_FOR_LATER_EXISTING_ITEM, new Error(errorMessage));

    failSpan(spanId, {
      errorType: 'HTTP_ERROR',
      status: response.status,
      statusText: response.statusText,
    });
    // Return error message instead of throwing
    throw new Error('There was an error calling save for later service');
  }

  const data = await response.json();

  // Check for API errors in successful responses
  if (data.formError || (data.formExceptions && data.formExceptions.length > 0)) {
    const errorMessages = data.formExceptions
      ? data.formExceptions.map((ex: any) => ex.localizedMessage).join(', ')
      : 'Unknown API error';

    const errorCodes = data.formExceptions ? data.formExceptions.map((ex: any) => ex.errorCode) : ['unknown'];

    (await loggerSFL).logWithCode(ErrorCodes.SAVE_FOR_LATER_SERVICE_ERROR, new Error(errorMessages));

    failSpan(spanId, {
      errorType: 'API_ERROR',
      errorCodes,
      messages: errorMessages,
    });
    // Return error message instead of throwing
    return 'There has been a technical issue. Please reload the page and try again or try again later.';
  }
  endSpan(spanId, { success: true });
  return data;
};

const checkProductExistsInSaveForLater = async (
  window: Window,
  csrfToken: string,
  sessionConfirmationNumber: string,
  productId: string,
): Promise<ATGSFLResponse> => {
  let saveForLaterList: ATGSFLResponse = {
    productExists: false,
  };
  try {
    saveForLaterList = await makeRequestToSaveForLaterATG(
      window,
      csrfToken,
      sessionConfirmationNumber,
      'exists',
      productId,
    );
  } catch (error) {
    console.error(error);
  }
  return saveForLaterList;
};

const addProductToSaveForLater = async (
  window: Window,
  csrfToken: string,
  sessionConfirmationNumber: string,
  productId: string,
) => {
  let saveForLaterData = {};
  try {
    saveForLaterData = makeRequestToSaveForLaterATG(
      window,
      csrfToken,
      sessionConfirmationNumber,
      'addProduct',
      productId,
      'POST',
    );
  } catch (error) {
    throw new Error(error);
  }
  return saveForLaterData;
};

const removeProductToSaveForLater = async (
  window: Window,
  csrfToken: string,
  sessionConfirmationNumber: string,
  productId: string,
) => {
  let saveForLaterData = {};
  try {
    saveForLaterData = makeRequestToSaveForLaterATG(
      window,
      csrfToken,
      sessionConfirmationNumber,
      'removeProduct',
      productId,
      'POST',
    );
  } catch (error) {
    console.error(error);
    throw new Error(error);
  }
  return saveForLaterData;
};

export default {
  checkProductExistsInSaveForLater,
  addProductToSaveForLater,
  removeProductToSaveForLater,
};
